import { Injectable, inject, Renderer2, RendererFactory2 } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { Branch } from './home.service';

@Injectable({
    providedIn: 'root'
})
export class SeoService {
    private titleService = inject(Title);
    private metaService = inject(Meta);
    private rendererFactory = inject(RendererFactory2);
    private renderer: Renderer2;
    private document = inject(DOCUMENT) as Document;

    constructor() {
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }

    /**
     * Updates the title and meta tags for the current page
     * @param title Page Title
     * @param description Meta Description
     * @param keywords Meta Keywords
     */
    updateSeoData(title: string, description: string, keywords: string, canonicalPath?: string) {
        this.titleService.setTitle(title);
        this.metaService.updateTag({ name: 'description', content: description });
        this.metaService.updateTag({ name: 'keywords', content: keywords });

        // Open Graph
        this.metaService.updateTag({ property: 'og:title', content: title });
        this.metaService.updateTag({ property: 'og:description', content: description });
        this.metaService.updateTag({ property: 'og:type', content: 'website' });
        this.metaService.updateTag({ property: 'og:image', content: 'https://www.vettrotraders.com/content/images/interior_fitouts_bg.webp' });
        this.metaService.updateTag({ property: 'og:site_name', content: 'Vettro Traders' });

        // Twitter Card
        this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.metaService.updateTag({ name: 'twitter:title', content: title });
        this.metaService.updateTag({ name: 'twitter:description', content: description });
        this.metaService.updateTag({ name: 'twitter:image', content: 'https://www.vettrotraders.com/content/images/interior_fitouts_bg.webp' });

        if (canonicalPath !== undefined) {
            this.setCanonicalURL(canonicalPath);
        }
    }

    /**
     * Sets the canonical URL for the current page ensuring no duplicate trailing slashes
     * @param path The URL path (e.g., from router)
     */
    setCanonicalURL(path?: string) {
        // Always use www. prefix — this MUST match what Google Search Console has verified
        const baseUrl = 'https://www.vettrotraders.com';
        let finalUrl: string;

        if (path !== undefined) {
            // path should start with '/' e.g. '/services'
            finalUrl = `${baseUrl}${path}`;
        } else {
            // Fallback: strip query/hash from the current document URL
            finalUrl = this.document.URL.split(/[?#]/)[0];
            // Replace any non-www variant
            finalUrl = finalUrl.replace('https://vettrotraders.com', baseUrl);
        }

        // Strip trailing slash unless root
        if (finalUrl !== baseUrl + '/' && finalUrl !== baseUrl && finalUrl.endsWith('/')) {
            finalUrl = finalUrl.slice(0, -1);
        }

        const existingLink: HTMLLinkElement | null = this.document.querySelector("link[rel='canonical']");
        if (existingLink) {
            this.renderer.setAttribute(existingLink, 'href', finalUrl);
        } else {
            const link: HTMLLinkElement = this.renderer.createElement('link');
            this.renderer.setAttribute(link, 'rel', 'canonical');
            this.renderer.setAttribute(link, 'href', finalUrl);
            this.renderer.appendChild(this.document.head, link);
        }

        // Also set og:url to match canonical
        this.metaService.updateTag({ property: 'og:url', content: finalUrl });
    }

    /**
     * Injects JSON-LD structured data into the head
     * @param data The structured data object
     */
    setJsonLd(data: any) {
        const script = this.renderer.createElement('script');
        script.type = 'application/ld+json';
        script.className = 'local-business-schema';
        script.text = JSON.stringify(data);

        // Remove existing LocalBusiness JSON-LD scripts to avoid duplicates
        const existingScripts = this.document.head.querySelectorAll('script.local-business-schema');
        existingScripts.forEach(s => this.renderer.removeChild(this.document.head, s));

        this.renderer.appendChild(this.document.head, script);
    }

    /**
     * Injects JSON-LD VideoObject Structured Data into the head
     * @param videos Array of VideoObject schemas
     */
    injectVideoSchema(videos: any[]) {
        if (!videos || videos.length === 0) return;

        const schema = {
            '@context': 'https://schema.org',
            '@graph': videos.map(video => ({
                '@type': 'VideoObject',
                ...video
            }))
        };

        const script = this.renderer.createElement('script');
        script.type = 'application/ld+json';
        script.className = 'video-schema';
        script.text = JSON.stringify(schema);

        // Remove existing Video Schema scripts to avoid duplicates
        const existingScripts = this.document.head.querySelectorAll('script.video-schema');
        existingScripts.forEach(s => this.renderer.removeChild(this.document.head, s));

        this.renderer.appendChild(this.document.head, script);
    }

    /**
     * Generates LocalBusiness Schema for branches
     * @param branches List of branches
     */
    generateLocalBusinessSchema(branches: Branch[]) {
        if (!branches || branches.length === 0) return;

        const schema = {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            'name': 'Vettro Traders',
            'url': 'https://vettrotraders.com',
            'logo': 'https://vettrotraders.com/assets/images/ready_install_units_bg.webp',
            'contactPoint': {
                '@type': 'ContactPoint',
                'telephone': '+91 944 777 2931',
                'contactType': 'customer service'
            },
            'subOrganization': branches.map(branch => ({
                '@type': 'HomeGoodsStore',
                'name': branch.name,
                'address': {
                    '@type': 'PostalAddress',
                    'streetAddress': branch.location,
                    'addressCountry': 'IN'
                },
                'telephone': branch.managerPhone,
                'priceRange': '₹₹'
            }))
        };

        this.setJsonLd(schema);
    }
}
